import { useSnackbar } from 'notistack';
import { useContext, useEffect, useReducer } from 'react';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { get } from '../Shared/ajax';
import GetAllDynamicWorkflowsResponse from './getAllDynamicWorkflowsResponse';
import LandingDynamicWorkflow from './landingDynamicWorkflow';
import LandingHeader from './LandingHeader';
import { initialState, landingReducer } from './landingReducer';
import LandingTableBody from './LandingTableBody';
import { LandingTableHeader } from './LandingTableHeader';
import { LandingActionKind } from './types';
import { AppContext } from '../App/appReducer';
import { AppActionKind, AppPageKind } from '../App/types';

const Landing = () => {
  const [landingState, landingDispatch] = useReducer(
    landingReducer,
    initialState
  );

  const { dispatch: appDispatch } = useContext(AppContext);

  const { enqueueSnackbar } = useSnackbar();

  const { dynamicWorkflows, isLoading, filteredWorkflows } = landingState;

  const showLoading = () => {
    landingDispatch({
      type: LandingActionKind.SET_ISLOADING,
      payload: true
    });
  };

  const hideLoading = () => {
    landingDispatch({
      type: LandingActionKind.SET_ISLOADING,
      payload: false
    });
  };

  const setDynamicWorkflows = (workflows: LandingDynamicWorkflow[]) => {
    landingDispatch({
      type: LandingActionKind.SET_DYNAMICWORKFLOWS,
      payload: workflows
    });
  };

  const fetchDynamicWorkflowsAsync = async () => {
    try {
      showLoading();

      const response = await get<GetAllDynamicWorkflowsResponse>(
        'DynamicWorkflow'
      );

      setDynamicWorkflows(response.dynamicWorkflows);
      hideLoading();
    } catch (err) {
      enqueueSnackbar('There was an unexpected error fetching the workflows', {
        variant: 'error'
      });
      hideLoading();
      return;
    }
  };

  const handleAddNewWorkflow = () => {
    appDispatch({
      type: AppActionKind.SET_APPPAGE,
      payload: AppPageKind.NEW_WORKFLOW
    });
  };

  const handleWorkflowSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const results = dynamicWorkflows.filter((s) =>
      s.name.toLowerCase().includes(event.target.value.toLowerCase())
    );

    landingDispatch({
      type: LandingActionKind.SEARCH_DYNAMICWORKFLOWS,
      payload: results
    });
  };

  useEffect(() => {
    fetchDynamicWorkflowsAsync();
  }, []);

  return (
    <Stack>
      <LandingHeader />
      <Stack
        sx={{ justifyContent: 'space-between', margin: '90px 30px 30px 30px' }}
      >
        <Grid item xs={12}>
          <Stack sx={{ my: 6, textAlign: 'center' }}>
            <Typography variant="h1">What would you like to do?</Typography>
          </Stack>
          <Stack>
            <Grid container>
              <Grid item xs={9} sx={{ textAlign: 'left' }}>
                <TextField
                  id="search-bar"
                  label="Filter by Workflow"
                  variant="outlined"
                  placeholder=""
                  size="small"
                  sx={{ '& label': { fontSize: 'large' } }}
                  onChange={handleWorkflowSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 'large' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={3} sx={{ textAlign: 'right' }}>
                <Button
                  variant="text"
                  onClick={handleAddNewWorkflow}
                  startIcon={<AddIcon />}
                >
                  Add New Workflow
                </Button>
              </Grid>
            </Grid>
          </Stack>
          <Stack sx={{ alignItems: 'left' }}>
            <Table>
              <LandingTableHeader />
              <LandingTableBody loading={isLoading} data={filteredWorkflows} />
            </Table>
          </Stack>
        </Grid>
      </Stack>
    </Stack>
  );
};

export default Landing;
